import React, { useEffect } from 'react';
import { HomeData, HomeImage, FeatureItem } from '../../types/home';
import OptimizedImage from '../about/OptimizedImage';

interface FeaturesSectionProps {
  mainImage: HomeImage | null;
  items: FeatureItem[];
  aboutData?: HomeData | null;
}

const FeaturesSection: React.FC<FeaturesSectionProps> = ({ mainImage, items, aboutData }) => {
  const aboutUrl = aboutData?.field_link_url?.[0]?.uri?.replace('internal:', '');

  const handleButtonClick = () => {
    if (aboutUrl) {
      window.location.href = aboutUrl;
    }
  };

  // Debug: log all items
  useEffect(() => {
    console.log('Features API items:', items);
    items.forEach((item, index) => {
      console.log(
        `Item ${index}: moderation_state =`,
        item.data?.moderation_state,
        '| position =',
        item.position,
        '| data =',
        item.data
      );
    });
  }, [items]);

  // Only published items (safe handling for array of objects)
  const publishedItems = items.filter(item => {
    const stateArray = item.data?.moderation_state;
    if (!Array.isArray(stateArray) || stateArray.length === 0) return false;
    
    const stateValue = stateArray[0]?.value;
    return typeof stateValue === 'string' && stateValue.toLowerCase() === 'published';
  });

  // Split by position
  const leftItems = publishedItems.filter(item => item.position === 'left');
  const rightItems = publishedItems.filter(item => item.position === 'right');

  return (
    <section id="features" className="features section">
      <div className="container">
        <div className="row gx-5 home-gro-feature">

          {/* Left Column */}
          {leftItems.length > 0 && (
            <div className="col-md-3 my-auto text-md-start first-column">
              <div className="row gx-2">
                {leftItems.map((item, index) => (
                  <div key={index} className="col-sm-12 d-md-flex d-sm-flex align-content-start home-feacture-div">
                    <div className="img-wrap">
                      {item.image && (
                        <OptimizedImage
                          src={item.image?.thumbnail?.[0]?.url}
                          className="img-fluid"
                          alt="Feature Icon"
                          loading="lazy"
                        />
                      )}
                    </div>
                    <div className="ps-lg-3">
                      <h6>{item.data?.field_description?.[0]?.value}</h6>
                      <p dangerouslySetInnerHTML={{ __html: item.data?.field_content?.[0]?.value || '' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Center Column */}
          <div className="col-md-6 second-column">
            {mainImage && (
              <OptimizedImage
                src={mainImage?.thumbnail?.[0]?.url}
                className="img-fluid"
                alt="Features Main Image"
                loading="lazy"
              />
            )}
          </div>

          {/* Right Column */}
          {rightItems.length > 0 && (
            <div className="col-md-3 my-auto third-column">
              <div className="row gx-2">
                {rightItems.map((item, index) => (
                  <div key={index} className="col-sm-12 right-column d-md-flex d-sm-flex align-content-start home-feacture-div">
                    <div className="img-wrap">
                      {item.image && (
                        <OptimizedImage
                          src={item.image?.thumbnail?.[0]?.url}
                          className="img-fluid"
                          alt="Feature Icon"
                          loading="lazy"
                        />
                      )}
                    </div>
                    <div className="pe-lg-3">
                      <h6>{item.data?.field_description?.[0]?.value}</h6>
                      <p dangerouslySetInnerHTML={{ __html: item.data?.field_content?.[0]?.value || '' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Button Section */}
        {aboutUrl && (
          <div className="row gx-2 mt-5">
            <div className="col-sm-12 text-center">
              <button
                type="button"
                onClick={handleButtonClick}
                className="btn btn-md btn-primary"
              >
                {aboutData?.field_link_url?.[0]?.title || 'Learn More'}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturesSection;
